<template>
  <div class="payment-links-page">
    <div class="action-buttons">
      <Button
        data-testid="payment-links-page-refresh-button"
        @click="loadData"
        :disabled="isLoading"
        severity="primary"
        size="small"
        :label="t('Atualizar')"
        :icon="isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'"
      />
    </div>

    <!-- PaymentLinksTable component -->
    <PaymentLinksTable
      ref="tableRef"
      :links="tableData"
      :loading="isLoading"
      :selection-enabled="true"
      @refresh="loadData"
      @view-link="editItem"
      @copy-link="copyLinkToClipboard"
      @cancel-link="cancelLink"
      @sync-link="syncLink"
      @batch-sync="batchSyncLinks"
    />

    <!-- PaymentLinkModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import PaymentLinksTable from './PaymentLinksTable.vue';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { usePaymentLinks } from '../../composables/usePaymentLinks';
import type { PaymentLinkRow, SyncResult, SyncSummary } from '../../composables/usePaymentLinks';
import { useConfirmAction } from '../../composables/useConfirmAction';

type Id = number | string;

interface PaymentLinksTableInstance {
  forceRefresh: () => void;
  getSelectedLinks: () => PaymentLinkRow[];
  setSelection: (links: PaymentLinkRow[]) => void;
  clearSelection: () => void;
}

// Get i18n composable
const { t, n } = useI18n();

// Get payment links composable
const { fetchPaymentLinks, deactivateLink, syncPaymentLink } = usePaymentLinks();
const { confirmAction } = useConfirmAction();

// Refs
const tableRef = ref<PaymentLinksTableInstance | null>(null);
const tableData = ref<PaymentLinkRow[]>([]);
const isLoading = ref<boolean>(false);

// Methods
const loadData = async (): Promise<void> => {
  log('PaymentLinksPage', 'loadData', 'Begin load');
  try {
    isLoading.value = true;
    eventBus.emit('loading.show', t('Carregando links de pagamento...'));

    const links = await fetchPaymentLinks();
    tableData.value = links;

    log('PaymentLinksPage', 'loadData', 'Loaded records', links.length);
    eventBus.emit('globalMessage.success', t('Carregados') + ` ${links.length} ` + t('links de pagamento'));
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    log('PaymentLinksPage', 'loadData', 'Error', message);
    eventBus.emit('globalMessage.error', t('Erro ao carregar links de pagamento: ') + message);
  } finally {
    isLoading.value = false;
    eventBus.emit('loading.hide');
  }
};

const editItem = (linkId: Id): void => {
  log('PaymentLinksPage', 'editItem', 'Viewing ID via eventBus', linkId);
  eventBus.emit('modal.payment-link.open', { id: linkId });
};

const copyLinkToClipboard = async (url: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(url);
    eventBus.emit('globalMessage.success', t('Link copiado para a area de transferencia'));
  } catch (error) {
    log('PaymentLinksPage', 'copyLinkToClipboard', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao copiar link'));
  }
};

const cancelLink = (linkId: Id): void => {
  log('PaymentLinksPage', 'cancelLink', 'Cancelling link', linkId);

  confirmAction({
    message: t('Tem certeza que deseja cancelar este link de pagamento?'),
    header: t('Confirmação'),
    onAccept: async () => {
      try {
        eventBus.emit('loading.show', t('Cancelando link de pagamento...'));
        await deactivateLink(Number(linkId));
        eventBus.emit('globalMessage.success', t('Link de pagamento cancelado com sucesso'));
        eventBus.emit('payment-link.cancelled');
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        log('PaymentLinksPage', 'cancelLink', 'Error', error);
        eventBus.emit('globalMessage.error', t('Erro ao cancelar link: ') + message);
      } finally {
        eventBus.emit('loading.hide');
      }
    }
  });
};

const syncLink = async (linkId: Id): Promise<void> => {
  log('PaymentLinksPage', 'syncLink', 'Syncing link', linkId);

  try {
    eventBus.emit('loading.show', t('Sincronizando link de pagamento...'));
    const result: SyncResult = await syncPaymentLink(Number(linkId));
    log('PaymentLinksPage', 'syncLink', 'Sync result', result);

    // Build message based on sync results
    const summary = result.summary || {};
    const chargesFound = summary.charges_found || 0;
    const chargesOk = summary.charges_processed_ok || 0;
    const chargesWithErrors = summary.charges_with_errors || 0;
    const totalPaid = summary.total_paid || 0;

    let message = '';
    let severity: 'success' | 'warning' | 'error' = 'success';

    if (chargesFound === 0) {
      message = t('Nenhuma cobrança encontrada para este link');
      severity = 'warning';
    } else if (chargesWithErrors > 0 && chargesOk === 0) {
      // All charges failed
      message = t('Erro ao sincronizar') + `: ${chargesWithErrors} ${t('cobranças com erro')}`;
      severity = 'error';
    } else if (chargesWithErrors > 0) {
      // Partial success
      message = t('Sincronização parcial') + `: ${chargesOk}/${chargesFound} ${t('cobranças processadas')}`;
      if (totalPaid > 0) {
        message += ` - ${t('Total pago')}: ${n(totalPaid, 'currency')}`;
      }
      severity = 'warning';
    } else {
      // Full success
      message = t('Sincronização concluída') + `: ${chargesOk} ${t('cobranças processadas')}`;
      if (totalPaid > 0) {
        message += ` - ${t('Total pago')}: ${n(totalPaid, 'currency')}`;
      }
      if (result.status_changed) {
        message += ` - ${t('Status')}: ${result.current_status}`;
      }
      severity = 'success';
    }

    // Emit globalMessage with appropriate severity
    if (severity === 'success') {
      eventBus.emit('globalMessage.success', message);
    } else if (severity === 'warning') {
      eventBus.emit('globalMessage.warning', message);
    } else {
      eventBus.emit('globalMessage.error', message);
    }

    // Emit event to trigger table reload
    eventBus.emit('payment-link.synced', {
      paymentLinkId: linkId,
      previousStatus: result.previous_status,
      newStatus: result.current_status,
      summary: summary
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    log('PaymentLinksPage', 'syncLink', 'Error', error);
    eventBus.emit('globalMessage.error', message || t('Erro ao sincronizar link de pagamento'));
  } finally {
    eventBus.emit('loading.hide');
  }
};

/**
 * Batch sync multiple payment links
 */
const batchSyncLinks = async (linkIds: Id[]): Promise<void> => {
  log('PaymentLinksPage', 'batchSyncLinks', 'Batch syncing links', { count: linkIds.length, linkIds });

  if (!linkIds || linkIds.length === 0) {
    eventBus.emit('globalMessage.warning', t('Nenhum link selecionado para sincronizar'));
    return;
  }

  // Show loading only once at the start
  eventBus.emit('loading.show', t('Sincronizando') + ` ${linkIds.length} ` + t('links de pagamento...'));

  try {
    // Track results
    let successCount = 0;
    let errorCount = 0;
    let totalChargesProcessed = 0;
    let totalPaidAmount = 0;
    const errors: { linkId: Id; error: string }[] = [];

    // Process each link sequentially to avoid overwhelming the API
    for (let i = 0; i < linkIds.length; i++) {
      const linkId = linkIds[i];
      log('PaymentLinksPage', 'batchSyncLinks', `Processing link ${i + 1}/${linkIds.length}`, { linkId });

      try {
        const result: SyncResult = await syncPaymentLink(Number(linkId));
        log('PaymentLinksPage', 'batchSyncLinks', 'Link synced', { linkId, result });

        successCount++;
        const summary = result.summary || {};
        totalChargesProcessed += (summary.charges_processed_ok || 0);
        totalPaidAmount += (summary.total_paid || 0);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        log('PaymentLinksPage', 'batchSyncLinks', 'Link sync error', { linkId, error: message });
        errorCount++;
        errors.push({ linkId, error: message });
      }
    }

    // Build final message
    let message = '';
    let severity: 'success' | 'warning' | 'error' = 'success';

    if (errorCount === 0) {
      // All successful
      message = t('Sincronização em lote concluída') + `: ${successCount} ${t('links sincronizados')}`;
      if (totalChargesProcessed > 0) {
        message += `, ${totalChargesProcessed} ${t('cobranças processadas')}`;
      }
      if (totalPaidAmount > 0) {
        message += ` - ${t('Total pago')}: ${n(totalPaidAmount, 'currency')}`;
      }
      severity = 'success';
    } else if (successCount === 0) {
      // All failed
      message = t('Erro ao sincronizar links') + `: ${errorCount} ${t('falhas')}`;
      severity = 'error';
    } else {
      // Partial success
      message = t('Sincronização parcial') + `: ${successCount} ${t('sucesso')}, ${errorCount} ${t('falhas')}`;
      if (totalPaidAmount > 0) {
        message += ` - ${t('Total pago')}: ${n(totalPaidAmount, 'currency')}`;
      }
      severity = 'warning';
    }

    // Emit globalMessage
    if (severity === 'success') {
      eventBus.emit('globalMessage.success', message);
    } else if (severity === 'warning') {
      eventBus.emit('globalMessage.warning', message);
    } else {
      eventBus.emit('globalMessage.error', message);
    }

    // Emit event to trigger table reload
    eventBus.emit('payment-link.synced', {
      batch: true,
      linkIds,
      successCount,
      errorCount,
      totalPaidAmount
    });

    log('PaymentLinksPage', 'batchSyncLinks', 'Batch sync completed', {
      successCount,
      errorCount,
      totalChargesProcessed,
      totalPaidAmount
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    log('PaymentLinksPage', 'batchSyncLinks', 'Batch sync error', error);
    eventBus.emit('globalMessage.error', message || t('Erro ao sincronizar links de pagamento'));
  } finally {
    eventBus.emit('loading.hide');
  }
};

// Event handlers with stable references for cleanup
const handleFilterUpdated = (): void => {
  log('PaymentLinksPage', 'event:filter.updated', 'Received');
  loadData();
};

const handleLinkCreated = (): void => {
  log('PaymentLinksPage', 'event:payment-link.created', 'Received');
  loadData();
};

const handleLinkCancelled = (): void => {
  log('PaymentLinksPage', 'event:payment-link.cancelled', 'Received');
  loadData();
};

const handleLinkSynced = (): void => {
  log('PaymentLinksPage', 'event:payment-link.synced', 'Received');
  loadData();
};

// Lifecycle hooks
onMounted(async () => {
  log('PaymentLinksPage', 'mounted', 'Component mounted, wiring handlers and loading initial data');

  // Load initial data
  await loadData();

  // Set up event listeners with proper references for cleanup
  eventBus.on('filter.updated', handleFilterUpdated);
  eventBus.on('payment-link.created', handleLinkCreated);
  eventBus.on('payment-link.cancelled', handleLinkCancelled);
  eventBus.on('payment-link.synced', handleLinkSynced);
});

onBeforeUnmount(() => {
  log('PaymentLinksPage', 'beforeUnmount', 'Cleaning up listeners');
  // Clean up event listeners properly with callback references
  eventBus.off('filter.updated', handleFilterUpdated);
  eventBus.off('payment-link.created', handleLinkCreated);
  eventBus.off('payment-link.cancelled', handleLinkCancelled);
  eventBus.off('payment-link.synced', handleLinkSynced);
});

// Expose public methods for parent components if needed
defineExpose({
  loadData,
  editItem
});
</script>

<style scoped>
.payment-links-page {
  width: 100%;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
  margin-bottom: 20px;
}

/* PrimeVue spinner animation */
:deep(.pi-spin) {
  animation: pi-spin 2s infinite linear;
}

@keyframes pi-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(359deg);
  }
}
</style>
